import { useEffect, useRef, useState } from "react";
import {
  broadcastCommand,
  createDisplay,
  deleteDisplay,
  listDisplays,
  listPlaylists,
  listSchedules,
  rotateDisplayToken,
  sendDisplayCommand,
  sendMachineCommand,
  updateDisplay,
  type DisplayCommand,
} from "../lib/resources.ts";
import type {
  AgentInfo,
  Display,
  MachineCommand,
  Playlist,
  Schedule,
} from "../lib/types.ts";

// Displays (admin-only) — the fleet dashboard. One card per TV: a live preview
// (real NUC screenshot when available, else a heartbeat-free player render),
// player + machine health, and remote controls (browser + machine level).
export default function Displays() {
  const [displays, setDisplays] = useState<Display[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  /** Id of a just-added display, so its card focuses the name field. */
  const [justAddedId, setJustAddedId] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const [d, s, p] = await Promise.all([
        listDisplays(),
        listSchedules(),
        listPlaylists(),
      ]);
      setDisplays(d);
      setSchedules(s);
      setPlaylists(p);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load displays");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void refresh();
    // Live health: re-pull display status every 15s without the loading flicker.
    const t = setInterval(() => {
      listDisplays().then(setDisplays).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, []);

  const onlineCount = displays.filter((d) => relativeSeen(d.lastSeenAt).alive).length;

  /**
   * Adds the display straight away; its card carries an editable name field, so
   * naming happens there rather than in a box beside the button.
   */
  async function create() {
    setCreating(true);
    setError(null);
    try {
      const d = await createDisplay("Untitled display", "landscape");
      await refresh();
      setJustAddedId(d.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn’t add the display");
    } finally {
      setCreating(false);
    }
  }

  return (
    <section className="admin-page">
      <div className="page-head">
        <div>
          <h1>Displays</h1>
          <p className="muted">
            Every screen at a glance — what it's showing, whether it's healthy,
            and remote controls. NUCs also report machine health + a live screenshot.
          </p>
          {displays.length > 0 && (
            <span className="health-summary">
              <span
                className={
                  "health-dot " +
                  (onlineCount === displays.length
                    ? "health-dot--ok"
                    : "health-dot--down")
                }
              >
                ●
              </span>
              {onlineCount} of {displays.length} online
            </span>
          )}
        </div>
        <div className="page-actions">
          <button
            className="btn btn--ghost"
            title="Reload every screen"
            onClick={() => broadcastCommand("reload")}
            disabled={displays.length === 0}
          >
            ⟳ Reload all
          </button>
          <button className="btn" onClick={create} disabled={creating}>
            {creating ? "Adding…" : "+ Add display"}
          </button>
        </div>
      </div>

      {error && <div className="banner banner--error">{error}</div>}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : displays.length === 0 ? (
        <div className="admin-empty">
          <strong>No displays yet</strong>
          <span>Add one, assign a schedule, then open its kiosk URL on the TV.</span>
        </div>
      ) : (
        <div className="display-grid">
          {displays.map((d) => (
            <DisplayCard
              key={d.id}
              display={d}
              schedules={schedules}
              playlists={playlists}
              onChange={refresh}
              autoFocusName={d.id === justAddedId}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function relativeSeen(iso: string | null): { label: string; alive: boolean } {
  if (!iso) return { label: "never", alive: false };
  const secs = (Date.now() - new Date(iso).getTime()) / 1000;
  const alive = secs < 120; // ~2 missed 30s polls
  if (secs < 60) return { label: "just now", alive };
  if (secs < 3600) return { label: `${Math.floor(secs / 60)}m ago`, alive };
  if (secs < 86400) return { label: `${Math.floor(secs / 3600)}h ago`, alive };
  return { label: `${Math.floor(secs / 86400)}d ago`, alive };
}

function fmtUptime(secs: number | null): string {
  if (secs == null) return "—";
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
  return `${Math.floor(secs / 86400)}d ${Math.floor((secs % 86400) / 3600)}h`;
}

function DisplayCard({
  display,
  schedules,
  playlists,
  onChange,
  autoFocusName = false,
}: {
  display: Display;
  schedules: Schedule[];
  playlists: Playlist[];
  onChange: () => void;
  /** Set for a just-added display: focus and select the name so it can be typed over. */
  autoFocusName?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocusName) nameRef.current?.select();
  }, [autoFocusName]);
  const [copied, setCopied] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const seen = relativeSeen(display.lastSeenAt);
  const agentSeen = relativeSeen(display.agentLastSeenAt);
  const agent = display.agentInfo;
  const hasAgent = display.agentLastSeenAt != null;
  const kioskUrl = `${window.location.origin}${display.playerPath}`;
  const shortUrl = display.shortPath
    ? `${window.location.origin}${display.shortPath}`
    : null;
  const tvUrl = shortUrl ?? kioskUrl;

  async function patch(body: Parameters<typeof updateDisplay>[1]) {
    setBusy(true);
    try {
      await updateDisplay(display.id, body);
      await onChange();
    } finally {
      setBusy(false);
    }
  }

  async function copyUrl() {
    await navigator.clipboard.writeText(tvUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  async function rotate() {
    if (!confirm("Rotate the token? The current kiosk URL stops working immediately."))
      return;
    setBusy(true);
    try {
      await rotateDisplayToken(display.id);
      await onChange();
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirm(`Delete display “${display.name}”?`)) return;
    await deleteDisplay(display.id);
    await onChange();
  }

  function flash(msg: string) {
    setNote(msg);
    setTimeout(() => setNote(null), 3000);
  }

  async function machine(kind: MachineCommand) {
    let payload: Record<string, unknown> | undefined;
    if (kind === "reboot" && !confirm("Reboot this machine now?")) return;
    if (kind === "update" && !confirm("Re-run setup and reboot this machine?")) return;
    if (kind === "repoint") {
      const code = prompt("Point this machine at which display short code?");
      if (!code) return;
      payload = { code: code.trim().toUpperCase() };
    }
    setBusy(true);
    try {
      await sendMachineCommand(display.id, kind, payload);
      flash(`Queued “${kind}” — runs on next check-in (~1 min).`);
    } catch (e) {
      flash(e instanceof Error ? e.message : "Command failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="display-card card">
      <div className="display-card-head">
        <input
          ref={nameRef}
          className="display-name"
          aria-label="Display name"
          placeholder="Display name"
          defaultValue={display.name}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v && v !== display.name) void patch({ name: v });
          }}
        />
        <span className={"health " + (seen.alive ? "health--ok" : "health--down")}>
          ● {seen.alive ? "online" : "offline"} · {seen.label}
        </span>
      </div>

      {/* ---- Live preview ---- */}
      <DisplayPreview display={display} onRefresh={() => machine("screenshot")} busy={busy} />

      {/* ---- Player status ---- */}
      <div className="tele-row">
        {display.playerStatus && (
          <span
            className={
              "tele-badge " +
              (display.playerStatus === "live"
                ? "tele-badge--ok"
                : display.playerStatus === "fallback"
                  ? "tele-badge--warn"
                  : "")
            }
          >
            {display.playerStatus}
            {display.playerMode ? ` · ${display.playerMode}` : ""}
          </span>
        )}
        {display.playerItemCount != null && (
          <span className="tele-meta">{display.playerItemCount} slides</span>
        )}
        {display.clientInfo?.w && (
          <span className="tele-meta">
            {display.clientInfo.w}×{display.clientInfo.h}
          </span>
        )}
      </div>

      {/* ---- Machine health (NUC agent) ---- */}
      {hasAgent && agent && (
        <MachineHealth agent={agent} agentSeenLabel={agentSeen.label} alive={agentSeen.alive} />
      )}

      <label className="field">
        <span>Schedule</span>
        <select
          value={display.scheduleId ?? ""}
          disabled={busy}
          onChange={(e) => patch({ scheduleId: e.target.value || null })}
        >
          <option value="">— none —</option>
          {schedules.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </label>

      <label className="field">
        <span>Default playlist (gap filler)</span>
        <select
          value={display.defaultPlaylistId ?? ""}
          disabled={busy}
          onChange={(e) => patch({ defaultPlaylistId: e.target.value || null })}
        >
          <option value="">— none —</option>
          {playlists.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </label>

      <label className="field">
        <span>Orientation</span>
        <select
          value={display.orientation}
          disabled={busy}
          onChange={(e) =>
            patch({ orientation: e.target.value as "landscape" | "portrait" })
          }
        >
          <option value="landscape">Landscape</option>
          <option value="portrait">Portrait</option>
        </select>
      </label>

      <div className="kiosk-url">
        {shortUrl ? (
          <>
            <div style={{ fontWeight: 600 }}>
              TV URL (type this): <code title={shortUrl}>{shortUrl}</code>
            </div>
            <div className="muted" style={{ fontSize: "0.8rem" }} title={kioskUrl}>
              full link also works (Copy URL)
            </div>
          </>
        ) : (
          <code title={kioskUrl}>{kioskUrl}</code>
        )}
      </div>

      <div className="display-card-actions">
        <a className="link-btn" href={display.playerPath} target="_blank" rel="noreferrer">
          Open
        </a>
        <button className="link-btn" onClick={copyUrl}>
          {copied ? "Copied!" : "Copy TV URL"}
        </button>
        <button className="link-btn" onClick={rotate} disabled={busy}>
          Rotate token
        </button>
        <button className="link-btn link-btn--danger" onClick={remove}>
          Delete
        </button>
      </div>

      <div className="display-commands">
        <span className="muted">Screen:</span>
        {(["reload", "refresh", "blank", "unblank"] as DisplayCommand[]).map(
          (action) => (
            <button
              key={action}
              className="cmd-btn"
              onClick={() => sendDisplayCommand(display.id, action)}
            >
              {action}
            </button>
          ),
        )}
      </div>

      {hasAgent ? (
        <div className="display-commands">
          <span className="muted">Machine:</span>
          <button className="cmd-btn" disabled={busy} onClick={() => machine("screenshot")}>
            screenshot
          </button>
          <button className="cmd-btn" disabled={busy} onClick={() => machine("restart-kiosk")}>
            restart sign
          </button>
          <button className="cmd-btn" disabled={busy} onClick={() => machine("reboot")}>
            reboot
          </button>
          <button className="cmd-btn" disabled={busy} onClick={() => machine("repoint")}>
            re-point
          </button>
          <button className="cmd-btn" disabled={busy} onClick={() => machine("update")}>
            update
          </button>
        </div>
      ) : (
        <div className="display-commands">
          <span className="muted" style={{ fontSize: "0.78rem" }}>
            No NUC agent — machine controls appear once a NUC checks in.
          </span>
        </div>
      )}

      {note && <div className="tele-note">{note}</div>}
    </div>
  );
}

function DisplayPreview({
  display,
  onRefresh,
  busy,
}: {
  display: Display;
  onRefresh: () => void;
  busy: boolean;
}) {
  const shotAge = relativeSeen(display.screenshotAt);
  // A real screenshot is only meaningful if reasonably fresh; otherwise fall
  // back to the live (heartbeat-free) player render so the preview is current.
  const useShot = display.screenshotUrl && display.screenshotAt;
  return (
    <div className="display-preview">
      {useShot ? (
        <img
          className="display-preview-media"
          src={display.screenshotUrl!}
          alt={`${display.name} screen`}
          loading="lazy"
        />
      ) : (
        <iframe
          className="display-preview-media"
          src={`${display.playerPath}&preview=1`}
          title={`${display.name} live preview`}
          loading="lazy"
        />
      )}
      <div className="display-preview-cap">
        {useShot ? (
          <>
            <span>📷 actual screen · {shotAge.label}</span>
            <button className="link-btn" disabled={busy} onClick={onRefresh}>
              refresh
            </button>
          </>
        ) : (
          <span>live preview</span>
        )}
      </div>
    </div>
  );
}

function MachineHealth({
  agent,
  agentSeenLabel,
  alive,
}: {
  agent: AgentInfo;
  agentSeenLabel: string;
  alive: boolean;
}) {
  const temp = agent.cpuTempC;
  return (
    <div className="machine-health">
      <div className="machine-health-head">
        <span className={"health " + (alive ? "health--ok" : "health--down")}>
          🖥 {agent.hostname ?? "NUC"} · {agentSeenLabel}
        </span>
        <span className={"tele-badge " + (agent.kioskAlive ? "tele-badge--ok" : "tele-badge--warn")}>
          {agent.kioskAlive ? "sign running" : "sign down"}
        </span>
      </div>
      <div className="machine-health-grid">
        <span>⏱ up {fmtUptime(agent.uptimeSeconds)}</span>
        {temp != null && (
          <span className={temp >= 85 ? "hot" : undefined}>🌡 {Math.round(temp)}°C</span>
        )}
        {agent.memAvailMb != null && <span>🧠 {(agent.memAvailMb / 1024).toFixed(1)}G free</span>}
        {agent.diskFreeGb != null && <span>💾 {agent.diskFreeGb}G free</span>}
        {agent.ip && <span>🌐 {agent.ip}</span>}
      </div>
      {agent.screenshotStatus === "failed" && (
        <div className="machine-health-warn" role="status">
          📷 screenshots failing{agent.screenshotError ? ` — ${agent.screenshotError}` : ""}
        </div>
      )}
    </div>
  );
}
